/**
 * Caption font substitution for a target language (PLAT-11409, epic
 * PLAT-11407 DE1/DE4). Pure helpers over the PLAT-11408 catalog, used at two
 * depths:
 *
 * - `getCaptionFontCorrection` — one config, at produce time
 *   (`applyCaptionsLanguage`): substitute the script-equivalent when the font
 *   can't render the target language, and RESTORE the recorded original when
 *   it covers the target again.
 * - `correctKaraokeDataFonts` — a whole persisted variant, at hydrate time
 *   (`KaraokeStore.hydrateLoadedByLanguage`): DE4 read-time correction of
 *   legacy variants, no data migration. Substitution here is limited to
 *   families we have information about (`isKnownCaptionFontFamily`, plus the
 *   caller-supplied picker catalog): a brand/custom font may well cover the
 *   script even though the catalog can't see it (until PLAT-11412), and
 *   re-correcting a deliberate pick on every load would be an unwinnable
 *   fight against the user.
 *
 * Reversibility rides on `KaraokeConfig.font_before_substitution`, which
 * records the user's own font AND the family we applied
 * (`applied_font_family`). The marker is only ACTIVE while the config still
 * carries exactly the applied family — a later manual pick deactivates it, so
 * restores and chained substitutions (ja → ko keying the preset table off the
 * original family) only ever rewrite fonts WE chose. A font that covers the
 * target script — including a manual user pick — is never touched (DE1).
 */

import {
  fontCoversScript,
  getCaptionFontSubstitute,
  isKnownCaptionFontFamily,
  normalizeCaptionFontFamilyKey,
} from '@src/utility/captionFonts';
import { resolveCaptionScript } from '@src/utility/captionLanguage';

import type { KaraokeConfig } from '../models/karaoke/KaraokeConfig';
import type { KaraokeData } from '../types/karaoke';
import type { CaptionScript } from '@src/utility/captionLanguage';

/** What changed, for the caller's user-facing notice. */
export type CaptionFontSubstitution = {
  /** The family now on the config. */
  fontFamily: string;
  /** The CANONICAL language key the correction was made for. */
  languageKey: string;
  /** True when a previously substituted font was restored. */
  restored: boolean;
};

type FontMarker = NonNullable<KaraokeConfig['font_before_substitution']>;

type CorrectableConfig = Pick<
  KaraokeConfig,
  'font_family' | 'font_weight' | 'font_style' | 'font_before_substitution'
>;

export type CaptionFontCorrection = {
  /** Spread over the config; omitted weight/style = keep the current one. */
  fields: Partial<CorrectableConfig>;
  substitution: CaptionFontSubstitution;
};

const restoreCorrection = (
  marker: FontMarker,
  languageKey: string,
): CaptionFontCorrection => ({
  fields: {
    font_family: marker.font_family,
    ...(marker.font_weight ? { font_weight: marker.font_weight } : {}),
    ...(marker.font_style ? { font_style: marker.font_style } : {}),
    font_before_substitution: undefined,
  },
  substitution: {
    fontFamily: marker.font_family,
    languageKey,
    restored: true,
  },
});

const correctionForScript = (
  config: CorrectableConfig,
  script: CaptionScript,
  languageKey: string,
  substitutionAllowed?: (family: string | null | undefined) => boolean,
): CaptionFontCorrection | null => {
  const marker = config.font_before_substitution;
  // Active = the config still carries exactly the family WE applied; a later
  // manual pick deactivates the marker (it lingers, inert, until a restore).
  const activeMarker =
    marker && marker.applied_font_family === config.font_family
      ? marker
      : undefined;

  if (script === 'latin') {
    // A Latin target always restores an active substitution (CJK faces
    // "cover" Latin glyph-wise but must not strand it stylistically).
    return activeMarker ? restoreCorrection(activeMarker, languageKey) : null;
  }

  if (activeMarker) {
    // Still our own substitute — re-derive for the new script off the user's
    // original font: restore it when it covers, else chain to its preset row.
    if (fontCoversScript(activeMarker.font_family, script)) {
      return restoreCorrection(activeMarker, languageKey);
    }
    const substitute = getCaptionFontSubstitute(
      activeMarker.font_family,
      script,
    );
    if (!substitute || substitute.font_family === config.font_family) {
      return null;
    }
    return {
      fields: {
        ...substitute,
        font_before_substitution: {
          ...activeMarker,
          applied_font_family: substitute.font_family,
        },
      },
      substitution: {
        fontFamily: substitute.font_family,
        languageKey,
        restored: false,
      },
    };
  }

  // DE1 — a covering font (including a manual pick) is never touched.
  if (fontCoversScript(config.font_family, script)) return null;
  if (substitutionAllowed && !substitutionAllowed(config.font_family)) {
    return null;
  }
  const substitute = getCaptionFontSubstitute(config.font_family, script);
  if (!substitute) return null;
  return {
    fields: {
      ...substitute,
      font_before_substitution: {
        font_family: config.font_family,
        font_weight: config.font_weight,
        font_style: config.font_style,
        applied_font_family: substitute.font_family,
      },
    },
    substitution: {
      fontFamily: substitute.font_family,
      languageKey,
      restored: false,
    },
  };
};

/**
 * The corrected font fields for `config` under `languageKey`, or `null` when
 * nothing needs to change. `languageKey` should be the CANONICAL key from
 * `resolveCaptionLanguageKey` whenever the caller has it — the
 * `'original'`/`'english'` sentinels resolve to `latin`, so passing the
 * resolved key is what makes a non-Latin ORIGINAL language (a Russian source
 * video) correct too (T2 DS1).
 */
export function getCaptionFontCorrection(
  config: CorrectableConfig,
  languageKey: string,
): CaptionFontCorrection | null {
  return correctionForScript(
    config,
    resolveCaptionScript(languageKey),
    languageKey,
  );
}

/**
 * Copy-based correction of every config in a persisted per-language karaoke
 * variant (DE4 read-time correction — see the module doc for the known-family
 * gate; `opts.knownFamilies` extends "known" with the app's own picker
 * catalog, which the catalog module can't import itself). The input is NEVER
 * mutated — variants are live store objects shared by reference (the same
 * invariant `cloneKaraokeConfigs` protects). Returns the input reference
 * unchanged when nothing needs correcting, so callers can cheaply detect
 * "no change".
 */
export function correctKaraokeDataFonts(
  data: KaraokeData,
  languageKey: string,
  opts?: { knownFamilies?: readonly string[] },
): KaraokeData {
  const script = resolveCaptionScript(languageKey);
  const extraKnown = new Set(
    (opts?.knownFamilies ?? []).map(normalizeCaptionFontFamilyKey),
  );
  const substitutionAllowed = (family: string | null | undefined): boolean =>
    isKnownCaptionFontFamily(family) ||
    extraKnown.has(normalizeCaptionFontFamilyKey(family));

  let configs: Map<string, KaraokeConfig> | null = null;
  for (const [id, config] of data.configs) {
    const correction = correctionForScript(
      config,
      script,
      languageKey,
      substitutionAllowed,
    );
    if (!correction) continue;
    configs ??= new Map(data.configs);
    configs.set(id, { ...config, ...correction.fields });
  }
  return configs ? { ...data, configs } : data;
}
